/**
 * Cliente HTTP del módulo de manifiestos.
 * Las rutas van sin /api porque axiosInstance ya usa REACT_APP_API_URL.
 */
import axios, { AxiosError } from 'axios'
import axiosInstance from '../../api/axiosInstance'
import type {
  Manifiesto,
  ManifiestoActivoData,
  ManifiestoPedidoLinea,
  ManifiestoUsuario,
} from '../../types/manifiestos'

export class ManifiestoApiError extends Error {
  code: string
  status: number
  payload: unknown

  constructor(message: string, code: string, status: number, payload: unknown) {
    super(message)
    this.code = code
    this.status = status
    this.payload = payload
  }
}

interface ApiBody {
  success?: boolean
  message?: string
  code?: string
  data?: unknown
}

function readBody(error: AxiosError): ApiBody {
  const data = error.response?.data
  return data && typeof data === 'object' ? (data as ApiBody) : {}
}

function toApiError(error: unknown, fallback: string): ManifiestoApiError {
  if (error instanceof ManifiestoApiError) return error
  if (axios.isAxiosError(error)) {
    const body = readBody(error)
    return new ManifiestoApiError(
      body.message || fallback,
      body.code || '',
      error.response?.status || 0,
      body
    )
  }
  if (error instanceof Error) {
    return new ManifiestoApiError(error.message, '', 0, null)
  }
  return new ManifiestoApiError(fallback, '', 0, null)
}

function unwrap<T>(body: ApiBody, fallback: string): T {
  if (body.success === false) {
    throw new ManifiestoApiError(body.message || fallback, body.code || '', 0, body)
  }
  return (body.data ?? body) as T
}

function asList(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value as Record<string, unknown>[]
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    const nested = record.manifiestos || record.items || record.rows
    if (Array.isArray(nested)) return nested as Record<string, unknown>[]
  }
  return []
}

export async function listarManifiestos(params: {
  transportadora?: string
  estado?: string
}): Promise<Manifiesto[]> {
  try {
    const response = await axiosInstance.get<ApiBody>('/manifiestos', {
      params: {
        transportadora: params.transportadora || undefined,
        estado: params.estado || undefined,
      },
    })
    const data = unwrap<unknown>(response.data, 'No se pudieron listar los manifiestos')
    return asList(data).map((item) => ({
      id: Number(item.id),
      estado: String(item.estado || ''),
      transportadora: String(item.transportadora || ''),
      consecutivo: item.consecutivo != null ? String(item.consecutivo) : undefined,
      fechaCreacion: String(
        item.fecha_creacion || item.fechaCreacion || item.created_at || item.createdAt || ''
      ),
      pedidos: Array.isArray(item.pedidos) ? item.pedidos.map(asPedido) : [],
      pedidosPorAgregar: Array.isArray(item.pedidos_por_agregar)
        ? item.pedidos_por_agregar.map((row) => {
            const pedido = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>
            return { numero_pedido: pedido.numero_pedido != null ? String(pedido.numero_pedido) : '' }
          })
        : [],
    }))
  } catch (error) {
    throw toApiError(error, 'No se pudieron listar los manifiestos')
  }
}

export async function getManifiestoActivo(
  transportadora: string
): Promise<ManifiestoActivoData> {
  try {
    const response = await axiosInstance.get<ApiBody>('/manifiestos/activo', {
      params: { transportadora },
    })
    return unwrap<ManifiestoActivoData>(response.data, 'No se pudo consultar el manifiesto')
  } catch (error) {
    throw toApiError(error, 'No se pudo consultar el manifiesto')
  }
}

export async function crearManifiesto(transportadora: string): Promise<Manifiesto> {
  try {
    const response = await axiosInstance.post<ApiBody>('/manifiestos', {
      transportadora,
    })
    const data = unwrap<Manifiesto | { manifiesto?: Manifiesto; id?: number }>(
      response.data,
      'No se pudo crear el manifiesto'
    )
    if (data && typeof data === 'object' && 'id' in data && typeof data.id === 'number') {
      return data as Manifiesto
    }
    const nested = (data as { manifiesto?: Manifiesto }).manifiesto
    if (nested?.id) return nested
    throw new ManifiestoApiError('La respuesta no incluye el id del manifiesto', '', 0, data)
  } catch (error) {
    throw toApiError(error, 'No se pudo crear el manifiesto')
  }
}

function asUsuario(value: unknown): ManifiestoUsuario | null {
  if (!value || typeof value !== 'object') return null
  const person = value as Record<string, unknown>
  if (person.id == null) return null
  return {
    id: Number(person.id),
    nombre: String(person.nombre || ''),
    apellido: String(person.apellido || ''),
  }
}

function asPedido(value: unknown): ManifiestoPedidoLinea {
  const row = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  return {
    id: row.id != null ? Number(row.id) : undefined,
    id_pedido: Number(row.id_pedido),
    numero_pedido: row.numero_pedido != null ? String(row.numero_pedido) : undefined,
    estado: row.estado != null ? String(row.estado) : undefined,
    guia: row.guia != null ? String(row.guia) : undefined,
    paquetes_verificados: Number(row.paquetes_verificados || 0),
    unidades_empaque: Number(row.unidades_empaque || 0),
    fecha_asignacion: row.fecha_asignacion != null ? String(row.fecha_asignacion) : null,
    fecha_ultima_verificacion: row.fecha_ultima_verificacion != null ? String(row.fecha_ultima_verificacion) : null,
    fecha_eliminacion: row.fecha_eliminacion != null ? String(row.fecha_eliminacion) : null,
    id_usuario_eliminacion: row.id_usuario_eliminacion != null ? Number(row.id_usuario_eliminacion) : null,
  }
}

export function normalizeManifiesto(raw: Record<string, unknown>): Manifiesto {
  const pedidos = Array.isArray(raw.pedidos) ? raw.pedidos.map(asPedido) : []
  return {
    id: Number(raw.id),
    estado: String(raw.estado || ''),
    transportadora: raw.transportadora != null ? String(raw.transportadora) : undefined,
    consecutivo: raw.consecutivo != null ? String(raw.consecutivo) : undefined,
    fechaCreacion: String(raw.fecha_creacion || raw.fechaCreacion || raw.created_at || ''),
    fechaModificacion: raw.fecha_modificacion != null ? String(raw.fecha_modificacion) : undefined,
    fechaInicioVerificacion: raw.fecha_inicio_verificacion != null ? String(raw.fecha_inicio_verificacion) : null,
    creadoPor: asUsuario(raw.creado_por),
    verificadoPor: asUsuario(raw.verificado_por),
    cerradoPor: asUsuario(raw.cerrado_por),
    pedidos,
  }
}

export async function getManifiesto(id: number): Promise<Manifiesto> {
  try {
    const response = await axiosInstance.get<ApiBody>(`/manifiestos/${id}`)
    const data = unwrap<unknown>(response.data, 'No se pudo consultar el manifiesto')
    const raw = data && typeof data === 'object' ? (data as Record<string, unknown>) : null
    const source = raw && 'id' in raw
      ? raw
      : raw && raw.manifiesto && typeof raw.manifiesto === 'object'
        ? (raw.manifiesto as Record<string, unknown>)
        : null
    if (!source || source.id == null) {
      throw new ManifiestoApiError('Manifiesto no encontrado', 'MANIFIESTO_NO_ENCONTRADO', 0, data)
    }
    return normalizeManifiesto(source)
  } catch (error) {
    const apiError = toApiError(error, 'No se pudo consultar el manifiesto')
    if (apiError.code === 'MANIFIESTO_NO_ENCONTRADO') {
      throw new ManifiestoApiError('No se encontró el manifiesto', 'MANIFIESTO_NO_ENCONTRADO', apiError.status, apiError.payload)
    }
    throw apiError
  }
}

export async function getPedidoDetalle(idPedido: number): Promise<unknown> {
  const response = await axiosInstance.get(`/list/${idPedido}`)
  const body = response.data as { data?: unknown }
  return body?.data ?? response.data
}

export async function guardarPinSupervisor(userId: number, pin: string): Promise<void> {
  try {
    await axiosInstance.put(`/manifiestos/supervisores/${userId}/pin`, { pin })
  } catch (error) {
    throw toApiError(error, 'No se pudo guardar el PIN')
  }
}
